import { getWithExpiry } from "util/common";
import { apiRequest } from "util/services";

const ETS_PLATFORM_PREFIX = {
  33: "ACS",
  34: "OTELPUAN",
  35: "SURVEY",
  36: "DOKTORTAKVIMI",
};

export function getPlatformJwtToken() {
  if (typeof window === "undefined") return null;
  return getWithExpiry("jwt_token");
}

/**
 * Map dashboard internalId → embed ETSReviews pk (+ optional sub-question id).
 *
 * Examples:
 * - 21528522_HOTEL     → SURVEY_21528522_HOTEL
 * - 21436094_3         → SURVEY_21436094_HOTEL + subQId 3
 * - 21436094           → SURVEY_21436094
 */
export function resolveEmbedReviewTarget(review) {
  const platformId = Number(review?.dash_platform ?? review?.PlatformId);
  const prefix = ETS_PLATFORM_PREFIX[platformId];
  const internalId = String(review?.internalId ?? review?.internal_id ?? "").trim();
  if (!prefix || !internalId) return null;

  const parts = internalId.split("_").filter(Boolean);
  if (!parts.length) return null;

  if (parts.length === 1) {
    return { reviewId: `${prefix}_${parts[0]}`, subQId: null };
  }

  const last = parts[parts.length - 1];
  if (/^\d+$/.test(last)) {
    const baseParts = parts.slice(0, -1);
    if (baseParts.length === 1 && prefix === "SURVEY") {
      return {
        reviewId: `${prefix}_${baseParts[0]}_HOTEL`,
        subQId: last,
      };
    }
    return {
      reviewId: `${prefix}_${baseParts.join("_")}`,
      subQId: last,
    };
  }

  return {
    reviewId: `${prefix}_${parts.join("_")}`,
    subQId: null,
  };
}

export function resolveEmbedReviewId(review) {
  return resolveEmbedReviewTarget(review)?.reviewId ?? null;
}

export function resolveEmbedSubQuestionId(review) {
  return resolveEmbedReviewTarget(review)?.subQId ?? null;
}

export function resolveEmbedBaseUrl() {
  if (process.env.REACT_APP_ENV === "production") {
    return "https://embed.pivony.com";
  }
  return "https://devembed.pivony.com";
}

export async function fetchEmbedFeedbackToken() {
  const result = await apiRequest("users/embed_feedback_token", "POST", {}, true);
  if (result?.success) return result.success;
  if (result?.access_token) return result.access_token;
  return null;
}

export function buildEmbedFeedbackUrl(review, token) {
  const target = resolveEmbedReviewTarget(review);
  if (!target?.reviewId || !token) return null;

  const params = new URLSearchParams();
  params.set("token", token);

  if (target.subQId) {
    params.set("subQId", target.subQId);
  }

  return `${resolveEmbedBaseUrl()}/review/${encodeURIComponent(target.reviewId)}?${params.toString()}`;
}

export function buildConsoleFeedbackUrl(review) {
  const dashId = review?.dash_id ?? review?.dashboard_id;
  if (!dashId || typeof window === "undefined") return null;

  const params = new URLSearchParams();
  if (review?.date_gpid) {
    params.set("date_gpid", review.date_gpid);
  }
  const qs = params.toString();
  return `${window.location.origin}/console/DashboardData/${dashId}${qs ? `?${qs}` : ""}`;
}

export async function resolveDrawerFeedbackUrlAsync(review) {
  if (resolveEmbedReviewTarget(review)) {
    const token = await fetchEmbedFeedbackToken();
    return buildEmbedFeedbackUrl(review, token);
  }
  return buildConsoleFeedbackUrl(review);
}

export function isEmbedFeedbackReview(review) {
  return Boolean(resolveEmbedReviewTarget(review));
}

export function canOpenDrawerFeedback(review) {
  if (isEmbedFeedbackReview(review)) {
    return Boolean(getPlatformJwtToken() && resolveEmbedReviewTarget(review));
  }
  return Boolean(buildConsoleFeedbackUrl(review));
}
